import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Star,
  Refresh,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import axios from "axios";
import logger from "../../utils/logger";

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    name_ar: "",
    price: "",
    duration_days: "30",
    max_users: "",
    max_clients: "",
    max_cases: "",
    storage_gb: "",
    features: "",
    description: "",
    is_active: true,
    sort_order: 0,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    const token = localStorage.getItem("superAdminToken");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/super-admin/plans`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlans(response.data.data);
    } catch (error) {
      logger.error("Error fetching plans:", error);
      showSnackbar("فشل تحميل الخطط", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      const limits = typeof plan.limits_json === 'string' 
        ? JSON.parse(plan.limits_json || '{}') 
        : (plan.limits_json || {});

      setFormData({
        name: plan.name,
        name_ar: plan.name_ar || "",
        price: plan.price,
        duration_days: "30", // Not stored in DB directly anymore, but kept for UI if needed or removed
        max_users: limits.users || "",
        max_clients: limits.clients || "",
        max_cases: limits.cases || "",
        storage_gb: limits.storage_gb || "",
        features: Array.isArray(plan.features) 
          ? plan.features.join("\n") 
          : (typeof plan.features === 'string' ? JSON.parse(plan.features || '[]').join("\n") : ""),
        description: plan.description || "",
        is_active: plan.is_active === 1,
        sort_order: plan.sort_order || 0,
      });
      setSelectedPlan(plan);
    } else {
      setFormData({
        name: "",
        name_ar: "",
        price: "",
        duration_days: "30",
        max_users: "",
        max_clients: "",
        max_cases: "",
        storage_gb: "",
        features: "",
        description: "",
        is_active: true,
        sort_order: 0,
      });
      setSelectedPlan(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPlan(null);
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem("superAdminToken");

    const limits_json = {
        users: parseInt(formData.max_users) || -1,
        clients: parseInt(formData.max_clients) || -1,
        cases: parseInt(formData.max_cases) || -1,
        storage_gb: parseInt(formData.storage_gb) || 1,
        sessions: -1, // Default or add field
        expenses: -1  // Default or add field
    };

    const data = {
      name: formData.name,
      name_ar: formData.name_ar,
      price: formData.price,
      description: formData.description,
      is_active: formData.is_active ? 1 : 0,
      sort_order: parseInt(formData.sort_order) || 0,
      features: formData.features.split("\n").filter((f) => f.trim() !== ""),
      limits_json: limits_json,
      billing_period: 'monthly' // Default for now
    };

    try {
      if (selectedPlan) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/super-admin/plans/${selectedPlan.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSnackbar("تم تحديث الخطة بنجاح", "success");
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/super-admin/plans`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSnackbar("تم إنشاء الخطة بنجاح", "success");
      }
      fetchPlans();
      handleCloseDialog();
    } catch (error) {
      logger.error("Error saving plan:", error);
      showSnackbar("فشل حفظ الخطة", "error");
    }
  };

  const handleToggleStatus = async (planId, currentStatus) => {
    const token = localStorage.getItem("superAdminToken");
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/super-admin/plans/${planId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar(
        `تم ${currentStatus ? "تعطيل" : "تفعيل"} الخطة بنجاح`,
        "success"
      );
      fetchPlans();
    } catch (error) {
      logger.error("Error toggling plan status:", error);
      showSnackbar("فشل تغيير حالة الخطة", "error");
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem("superAdminToken");
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/super-admin/plans/${selectedPlan.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar("تم حذف الخطة بنجاح", "success");
      fetchPlans();
      setConfirmDialog(false);
      setSelectedPlan(null);
    } catch (error) {
      logger.error("Error deleting plan:", error);
      showSnackbar(error.response?.data?.message || "فشل حذف الخطة", "error");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("ar-IQ").format(price) + " IQD";
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          إدارة الخطط
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchPlans}
          >
            تحديث
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            إضافة خطة جديدة
          </Button>
        </Box>
      </Box>

      {loading ? (
        <LinearProgress />
      ) : (
        <Grid container spacing={3}>
          {plans.length === 0 ? (
            <Grid item xs={12}>
              <Paper sx={{ p: 5, textAlign: "center" }}>
                <Typography color="text.secondary">
                  لا توجد خطط، قم بإنشاء خطة جديدة
                </Typography>
              </Paper>
            </Grid>
          ) : (
            plans.map((plan) => {
               const limits = typeof plan.limits_json === 'string' 
                ? JSON.parse(plan.limits_json || '{}') 
                : (plan.limits_json || {});
               const features = Array.isArray(plan.features) 
                ? plan.features 
                : (typeof plan.features === 'string' ? JSON.parse(plan.features || '[]') : []);

               return (
              <Grid item xs={12} md={6} lg={4} key={plan.id}>
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h5" fontWeight="bold">
                        {plan.name_ar || plan.name}
                      </Typography>
                      <Chip
                        label={plan.is_active ? "نشط" : "معطل"}
                        color={plan.is_active ? "success" : "default"}
                        size="small"
                      />
                    </Box>

                    <Typography
                      variant="h3"
                      color="primary"
                      fontWeight="bold"
                      mb={1}
                    >
                      {formatPrice(plan.price)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      دفع {plan.billing_period === 'monthly' ? 'شهري' : 'سنوي'}
                    </Typography>

                    {plan.description && (
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {plan.description}
                      </Typography>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                      المميزات:
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary={`عدد المستخدمين: ${
                            !limits.users || limits.users === -1 ? "غير محدود" : limits.users
                          }`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={`عدد الموكلين: ${
                            !limits.clients || limits.clients === -1
                              ? "غير محدود"
                              : limits.clients
                          }`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={`عدد القضايا: ${
                           !limits.cases || limits.cases === -1 ? "غير محدود" : limits.cases
                          }`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={`مساحة التخزين: ${
                            !limits.storage_gb || limits.storage_gb === -1
                              ? "غير محدودة"
                              : `${limits.storage_gb} GB`
                          }`}
                        />
                      </ListItem>
                    </List>

                    {features && features.length > 0 && (
                      <>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          mt={2}
                          mb={1}
                        >
                          مميزات إضافية:
                        </Typography>
                        <List dense>
                          {features.map((feature, index) => (
                            <ListItem key={index}>
                              <CheckCircle
                                fontSize="small"
                                color="success"
                                sx={{ mr: 1 }}
                              />
                              <ListItemText primary={feature} />
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => handleOpenDialog(plan)}
                    >
                      تعديل
                    </Button>
                    <Button
                      size="small"
                      startIcon={
                        plan.is_active ? <VisibilityOff /> : <Visibility />
                      }
                      onClick={() =>
                        handleToggleStatus(plan.id, plan.is_active)
                      }
                    >
                      {plan.is_active ? "تعطيل" : "تفعيل"}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => {
                        setSelectedPlan(plan);
                        setConfirmDialog(true);
                      }}
                    >
                      حذف
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
            })
          )}
        </Grid>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPlan ? "تعديل الخطة" : "إضافة خطة جديدة"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="اسم الخطة (English)"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="اسم الخطة (عربي)"
                  value={formData.name_ar}
                  onChange={(e) =>
                    setFormData({ ...formData, name_ar: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="السعر"
                  type="number"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">IQD</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                 <TextField
                  fullWidth
                  label="الترتيب"
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) =>
                    setFormData({ ...formData, sort_order: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="عدد المستخدمين"
                  type="number"
                  value={formData.max_users}
                  onChange={(e) =>
                    setFormData({ ...formData, max_users: e.target.value })
                  }
                  required
                  helperText="-1 لعدد غير محدود"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="عدد الموكلين"
                  type="number"
                  value={formData.max_clients}
                  onChange={(e) =>
                    setFormData({ ...formData, max_clients: e.target.value })
                  }
                  required
                  helperText="-1 لعدد غير محدود"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="عدد القضايا"
                  type="number"
                  value={formData.max_cases}
                  onChange={(e) =>
                    setFormData({ ...formData, max_cases: e.target.value })
                  }
                  required
                  helperText="-1 لعدد غير محدود"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="مساحة التخزين (GB)"
                  type="number"
                  value={formData.storage_gb}
                  onChange={(e) =>
                    setFormData({ ...formData, storage_gb: e.target.value })
                  }
                  required
                  helperText="-1 لمساحة غير محدودة"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="الوصف"
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="المميزات الإضافية (كل ميزة في سطر)"
                  multiline
                  rows={4}
                  value={formData.features}
                  onChange={(e) =>
                    setFormData({ ...formData, features: e.target.value })
                  }
                  placeholder="مثال:&#10;دعم فني على مدار الساعة&#10;تقارير مخصصة&#10;نسخ احتياطي يومي"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_active: e.target.checked,
                        })
                      }
                    />
                  }
                  label="تفعيل الخطة"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>إلغاء</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedPlan ? "تحديث" : "إضافة"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من حذف الخطة "{selectedPlan?.name}"؟
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            لا يمكن حذف الخطة إذا كانت مرتبطة باشتراكات نشطة
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>إلغاء</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};


export default Plans;
